/**
 * Free gift with purchase (Lisa Corti)
 * Settings and texts: snippets/free-gift-config.liquid
 *
 * 1. Watches every cart change made through fetch (theme, quick add, apps).
 * 2. Adds the gift when the cart total (after discounts, gift and gift wrap excluded)
 *    reaches the threshold, removes it when the total drops below,
 *    keeps it at quantity 1.
 * 3. Shows a message bar at the top of the cart drawer and the cart page.
 * 4. Adds the gift's description under the gift line, cut to one line; a click opens it
 *    (template FreeGiftNote in the config).
 * 5. Hides discount labels allocated 0 on a line (the "buy X" item of the gift discount):
 *    decided from cart.js data, so it does not depend on the discount's name.
 * The gift line itself (badge, no quantity, no remove) is styled in CSS
 * from the config snippet.
 */
(() => {
  if (window.__lcFreeGiftInit) return;
  window.__lcFreeGiftInit = true;

  let CFG = null;
  try {
    CFG = JSON.parse(document.getElementById('FreeGiftConfig')?.textContent || 'null');
  } catch (e) {
    CFG = null;
  }
  if (!CFG || !CFG.variantId) return;

  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const url = path => `${root}${path}`;
  const nativeFetch = window.fetch.bind(window);
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?\/?$/;

  /* ---------------- helpers ---------------- */

  const thresholdFor = currency => {
    const base = Math.round(Number(CFG.threshold || 200) * 100);
    const cur = String(currency || '').toUpperCase();
    if ((CFG.localCurrencies || []).includes(cur)) return base;
    // other currencies: EUR threshold at the Shopify rate, rounded to
    // 2 significant digits so the customer sees a round number
    // (e.g. 36.289 JPY -> 36.000, 2.290 SEK -> 2.300)
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const major = (base / 100) * rate;
    if (!(major > 0)) return base;
    const step = Math.pow(10, Math.max(0, Math.floor(Math.log10(major)) - 1));
    return Math.round(major / step) * step * 100;
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (e) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  };

  const isGift = item => item.variant_id === CFG.variantId || item.product_id === CFG.productId;
  // services that do not count toward the threshold (gift wrap)
  const EXCLUDED = (CFG.excludeSkus || []).map(s => String(s).toUpperCase());
  const isExcluded = item => EXCLUDED.includes(String(item.sku || '').toUpperCase());

  function analyse(cart) {
    const items = cart.items || [];
    const giftItems = items.filter(isGift);
    const excludedItems = items.filter(item => !isGift(item) && isExcluded(item));
    const hasOthers = items.some(item => !isGift(item) && !isExcluded(item));
    const giftQty = giftItems.reduce((sum, item) => sum + item.quantity, 0);
    const giftTotal = giftItems.reduce((sum, item) => sum + item.final_line_price, 0);
    const excludedTotal = excludedItems.reduce((sum, item) => sum + item.final_line_price, 0);
    const eligible = cart.total_price - giftTotal - excludedTotal;
    const threshold = thresholdFor(cart.currency);
    return {
      giftItems,
      giftQty,
      hasOthers,
      eligible,
      threshold,
      currency: cart.currency,
      qualifies: hasOthers && eligible >= threshold
    };
  }

  /* ---------------- message bar ---------------- */

  let barState = null; // { text, progress } or null when nothing to show

  function computeBar(a) {
    if (!a.hasOthers) return null;
    if (a.giftQty > 0) return { text: CFG.textSuccess, progress: 100 };
    if (!CFG.available || a.eligible >= a.threshold) return null;
    return {
      text: String(CFG.textProgress || '').replace('[amount]', formatMoney(a.threshold - a.eligible, a.currency)),
      progress: Math.max(0, Math.min(100, (a.eligible / a.threshold) * 100))
    };
  }

  function buildBar(state) {
    const bar = document.createElement('div');
    bar.className = 'free-gift-bar progress-bar';
    bar.setAttribute('role', 'status');
    const top = document.createElement('div');
    top.className = 'progress-bar__top';
    const icon = document.getElementById('FreeGiftIcon');
    if (icon) top.appendChild(icon.content.cloneNode(true));
    const p = document.createElement('p');
    p.className = 'progress-bar__text';
    p.textContent = state.text;
    top.appendChild(p);
    const track = document.createElement('div');
    track.className = 'progress-bar__progress';
    const line = document.createElement('span');
    line.className = 'progress-bar__progress-inner';
    line.style.width = `calc(${state.progress}% + 2px)`;
    track.appendChild(line);
    bar.append(top, track);
    bar.dataset.text = state.text;
    return bar;
  }

  // where the bar goes: [container, element to insert before]
  function barSlots() {
    const slots = [];
    const drawerMain = document.querySelector('#CartDrawer-Body .cart-drawer__main');
    if (drawerMain) slots.push([drawerMain.parentElement, drawerMain]);
    const pageHead = document.querySelector('#main-cart-items .cart__items__head');
    if (pageHead) slots.push([pageHead.parentElement, pageHead]);
    return slots;
  }

  function renderBars() {
    barSlots().forEach(([container, before]) => {
      const existing = container.querySelector(':scope > .free-gift-bar');
      if (!barState) {
        if (existing) existing.remove();
        return;
      }
      if (existing && existing.dataset.text === barState.text) return;
      if (existing) existing.remove();
      container.insertBefore(buildBar(barState), before);
    });
  }

  /* ---------------- description under the gift line ---------------- */

  const noteTemplate = document.getElementById('FreeGiftNote');
  const giftPath = `/products/${CFG.handle}`;

  function renderNotes() {
    if (!noteTemplate) return;
    document.querySelectorAll('.cart-item').forEach(row => {
      if (row.querySelector('.free-gift-note')) return;
      const link = row.querySelector('.cart-item__title a');
      if (!link) return;
      let path = '';
      try {
        path = new URL(link.getAttribute('href') || '', window.location.origin).pathname;
      } catch (e) {
        return;
      }
      if (!path.endsWith(giftPath)) return;
      const note = document.createElement('div');
      note.className = 'free-gift-note';
      note.textContent = noteTemplate.content.textContent.trim();
      note.title = noteTemplate.dataset.more || '';
      note.setAttribute('role', 'button');
      note.setAttribute('tabindex', '0');
      note.setAttribute('aria-expanded', 'false');
      const toggle = () => {
        const open = note.classList.toggle('is-open');
        note.setAttribute('aria-expanded', String(open));
      };
      note.addEventListener('click', toggle);
      note.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      });
      link.closest('.cart-item__title').insertAdjacentElement('afterend', note);
    });
  }

  /* ---------------- discount labels allocated 0 ---------------- */

  let lastCart = null; // last cart.js answer, for the labels

  function renderDiscountLabels() {
    if (!lastCart) return;
    document.querySelectorAll('.cart-item').forEach(row => {
      // rows are CartItem-1, CartItem-2... in cart.items order (page and drawer alike)
      const index = parseInt(String(row.id || '').replace('CartItem-', ''), 10) - 1;
      const item = lastCart.items && lastCart.items[index];
      if (!item) return;
      const zero = new Set(
        (item.discounts || []).filter(d => !d.amount).map(d => String(d.title || '').trim().toLowerCase())
      );
      row.querySelectorAll('.cart-item__discounts li').forEach(li => {
        const label = (li.querySelector('span') || li).textContent.trim().toLowerCase();
        const hide = zero.has(label);
        li.hidden = hide;
        li.style.display = hide ? 'none' : '';
      });
    });
  }

  // the theme replaces the cart HTML after every change: put the bar, the note and the labels back
  new MutationObserver(() => {
    renderBars();
    renderNotes();
    renderDiscountLabels();
  }).observe(document.body, { childList: true, subtree: true });
  renderNotes();
  renderDiscountLabels();

  /* ---------------- cart reconciliation ---------------- */

  let timer = null;
  let running = false;
  let rerun = false;
  let addBlocked = false;

  const getSections = () =>
    typeof sectionsToRender !== 'undefined' && Array.isArray(sectionsToRender) ? sectionsToRender : [];

  function renderSections(sectionsHtml) {
    if (sectionsHtml) {
      getSections().forEach(section => {
        const html = sectionsHtml[section.section];
        if (!html) return;
        const target = document.querySelector(section.selector) || document.querySelector(section.id);
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (target && source) target.innerHTML = source.innerHTML;
      });
    }
    if (typeof updateCartCounters === 'function') updateCartCounters();
    if (typeof updateFreeShipping === 'function') updateFreeShipping();
  }

  async function getCart() {
    const res = await nativeFetch(url('cart.js'), { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function postJSON(path, body) {
    const res = await nativeFetch(url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data };
  }

  async function reconcile() {
    if (running) {
      rerun = true;
      return;
    }
    running = true;
    try {
      let cart = await getCart();
      const a = analyse(cart);

      const sections = getSections().map(section => section.section);
      const extra = sections.length ? { sections, sections_url: window.location.pathname } : {};
      let changed = false;

      if (a.qualifies && a.giftQty === 0 && CFG.available && !addBlocked) {
        const r = await postJSON('cart/add.js', {
          items: [{ id: CFG.variantId, quantity: 1, properties: { _free_gift: 'auto' } }],
          ...extra
        });
        if (r.ok) {
          renderSections(r.data.sections);
          changed = true;
        } else {
          addBlocked = true; // sold out or unavailable: stop trying on this page
        }
      } else if (a.giftQty > 0 && (!a.qualifies || a.giftQty > 1)) {
        const updates = {};
        a.giftItems.forEach((item, i) => {
          updates[item.key] = a.qualifies && i === 0 ? 1 : 0;
        });
        const r = await postJSON('cart/update.js', { updates, ...extra });
        if (r.ok) {
          renderSections(r.data.sections);
          changed = true;
        }
      }

      if (changed) cart = await getCart();
      lastCart = cart;
      barState = computeBar(analyse(cart));
      renderBars();
      renderDiscountLabels();
    } catch (e) {
      console.error('[free-gift]', e);
    } finally {
      running = false;
      if (rerun) {
        rerun = false;
        schedule();
      }
    }
  }

  function schedule() {
    clearTimeout(timer);
    // let the theme finish rendering its own cart update first
    timer = setTimeout(reconcile, 400);
  }

  window.fetch = function (input, init) {
    const promise = nativeFetch(input, init);
    try {
      const raw = typeof input === 'string' ? input : (input && input.url) || '';
      const path = new URL(raw, window.location.origin).pathname;
      const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method === 'POST' && CART_MUTATION.test(path)) promise.then(schedule, schedule);
    } catch (e) {
      /* ignore */
    }
    return promise;
  };

  // labels first (no wait), then the usual reconciliation
  getCart()
    .then(cart => {
      lastCart = cart;
      renderDiscountLabels();
    })
    .catch(() => {});
  schedule();
})();
